import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Truck, Store, ArrowRight, Wallet, Plus,
  MapPin, Phone, User, Clock, ChevronRight, ChevronLeft, ChevronDown, Copy, ExternalLink,
  FileText, Camera, Zap, Package, CheckCircle2, PenLine, Receipt, SkipForward, Globe, Pencil, Bike,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { API } from "@/lib/api";

const STATUS_LABELS = {
  new: "New", search: "Search",
  pickup_assignment_needed: "Pickup Assignment Needed", pickup_assigned: "Pickup Assigned", pickup_complete: "Pickup Complete",
  at_merchant: "At Merchant", in_service: "In Service",
  invoice_generated: "Invoice Generated", signature_pending: "Signature Pending",
  payout_pending: "Payout Pending",
  delivery_assignment_needed: "Delivery Assignment Needed",
  delivery_assigned: "Delivery Assigned", delivered: "Delivered", closed: "Closed",
};

const STATUS_COLORS = {
  new: "bg-slate-100 text-slate-700",
  search: "bg-violet-100 text-violet-700",
  pickup_assignment_needed: "bg-blue-50 text-blue-600",
  pickup_assigned: "bg-blue-100 text-blue-700", pickup_complete: "bg-blue-100 text-blue-700",
  at_merchant: "bg-amber-100 text-amber-700", in_service: "bg-amber-100 text-amber-700",
  invoice_generated: "bg-orange-100 text-orange-700", signature_pending: "bg-orange-100 text-orange-700",
  payout_pending: "bg-pink-100 text-pink-700",
  delivery_assignment_needed: "bg-cyan-50 text-cyan-600",
  delivery_assigned: "bg-cyan-100 text-cyan-700", delivered: "bg-emerald-100 text-emerald-700",
  closed: "bg-emerald-100 text-emerald-700",
};

const JOURNEY_LABELS = {
  home_to_shop: "Home → Shop → Home",
  shop_to_home: "Shop → Home",
  c2c: "C2C",
};

// Per-journey next status (null = needs rider assignment or special action)
const NEXT_MAP = {
  home_to_shop: {
    new: "search",
    search: "pickup_assignment_needed",
    pickup_assignment_needed: null,    // needs pickup rider
    pickup_assigned: "pickup_complete",  // rider picks up from customer
    pickup_complete: "at_merchant",      // rider drops off at merchant
    at_merchant: "invoice_generated",    // skip in_service
    in_service: "invoice_generated",   // legacy orders
    invoice_generated: null,           // needs payout mark (show payout form)
    signature_pending: null,           // legacy — show payout form
    payout_pending: "delivery_assignment_needed",  // after payout → assign delivery
    delivery_assignment_needed: null,  // needs delivery rider
    delivery_assigned: "delivered",    // rider marks delivered; RM then closes
    delivered: "closed",
    closed: null,
  },
  shop_to_home: {
    new: "search",
    search: "pickup_assignment_needed",
    pickup_assignment_needed: null,      // needs pickup rider assignment
    pickup_assigned: "at_merchant",        // rider collects from shop
    at_merchant: "invoice_generated",      // skip in_service
    in_service: "invoice_generated",       // legacy orders
    invoice_generated: null,               // needs payout mark (show payout form)
    signature_pending: null,               // legacy — show payout form
    payout_pending: "delivery_assignment_needed",  // after payout → assign delivery
    delivery_assignment_needed: "delivery_assigned",  // S2H: auto-assigns pickup rider
    delivery_assigned: "delivered",        // rider marks delivered; RM then closes
    delivered: "closed",
    closed: null,
  },
  c2c: {
    new: "search",
    search: "pickup_assignment_needed",
    pickup_assignment_needed: null,  // needs pickup rider
    pickup_assigned: "pickup_complete",
    pickup_complete: "delivered",      // rider sends for delivery; RM then closes
    delivery_assigned: "delivered",    // legacy orders
    delivered: "closed",
    closed: null,
  },
};

const ACTION_LABELS = {
  new: "Start Search",
  search: "Mark Search Done",
  payout_pending: "Proceed to Delivery",
  pickup_assigned: "Mark Picked Up",     // rider picks up from customer
  pickup_complete: "Mark at Merchant",   // rider drops off at merchant
  at_merchant: "Generate Invoice",       // skip in_service
  in_service: "Generate Invoice",        // legacy orders
  signature_pending: "Mark Invoice Signed", // legacy orders
  delivery_assigned: "Mark Delivered",
  delivered: "Close Order",
};

// Previous status map (for "Move Back" feature) — mirrors backend TRANSITIONS second element
const PREV_MAP = {
  home_to_shop: {
    new: null,
    search: "new",
    pickup_assignment_needed: "search",
    pickup_assigned: "pickup_assignment_needed",
    pickup_complete: "pickup_assigned",
    at_merchant: "pickup_complete",       // restore pickup_complete step
    in_service: "at_merchant",            // legacy
    invoice_generated: "at_merchant",     // skip in_service
    signature_pending: "invoice_generated", // legacy
    payout_pending: "invoice_generated",  // skip signature_pending
    delivery_assignment_needed: "payout_pending",
    delivery_assigned: "delivery_assignment_needed",
    delivered: "delivery_assigned",
    closed: "delivered",
  },
  shop_to_home: {
    new: null,
    search: "new",
    pickup_assignment_needed: "search",
    pickup_assigned: "pickup_assignment_needed",
    at_merchant: "pickup_assigned",
    in_service: "at_merchant",              // legacy
    invoice_generated: "at_merchant",       // skip in_service
    signature_pending: "invoice_generated", // legacy
    payout_pending: "invoice_generated",    // skip signature_pending
    delivery_assignment_needed: "payout_pending",
    delivery_assigned: "delivery_assignment_needed",
    delivered: "delivery_assigned",
    closed: "delivered",
  },
  c2c: {
    new: null,
    search: "new",
    pickup_assignment_needed: "search",
    pickup_assigned: "pickup_assignment_needed",
    pickup_complete: "pickup_assigned",
    delivery_assigned: "pickup_complete", // legacy
    delivered: "pickup_complete",
    closed: "delivered",
  },
};

// C2C overrides
const ACTION_LABELS_C2C = {
  ...ACTION_LABELS,
  pickup_assigned: "Mark Pickup Complete", // C2C: pickup_assigned → pickup_complete
  pickup_complete: "Mark Delivered",       // C2C: rider sends for delivery → delivered
  delivery_assigned: "Mark Delivered",     // legacy C2C orders
};

// S2H overrides: same as H2S — rider assigned first, then invoice, then delivery
const ACTION_LABELS_S2H = {
  ...ACTION_LABELS,
  pickup_assigned: "Mark at Merchant",     // S2H: rider collected from shop
  delivery_assignment_needed: "Start Delivery",  // S2H: auto-assigns pickup rider as delivery rider
};

const SKIP_MAP = {
  home_to_shop: {
    new: "pickup_assignment_needed",
    search: "pickup_assigned",
    pickup_assignment_needed: "pickup_assigned",
    pickup_assigned: "pickup_complete",
    pickup_complete: "at_merchant",
    at_merchant: "delivery_assignment_needed",
    invoice_generated: "delivery_assignment_needed",
    payout_pending: "delivery_assigned",
    delivery_assignment_needed: "delivery_assigned",
    delivery_assigned: "delivered",
    delivered: "closed",
  },
  shop_to_home: {
    new: "pickup_assignment_needed",
    search: "pickup_assigned",
    pickup_assignment_needed: "pickup_assigned",
    pickup_assigned: "at_merchant",
    at_merchant: "delivery_assignment_needed",
    invoice_generated: "delivery_assignment_needed",
    payout_pending: "delivery_assigned",
    delivery_assignment_needed: "delivery_assigned",
    delivery_assigned: "delivered",
    delivered: "closed",
  },
  c2c: {
    new: "pickup_assignment_needed",
    search: "pickup_assigned",
    pickup_assignment_needed: "pickup_assigned",
    pickup_assigned: "pickup_complete",
    pickup_complete: "delivered",
  },
};

export const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [proofs, setProofs] = useState([]);
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedRiderId, setSelectedRiderId] = useState("");
  const [payoutRef, setPayoutRef] = useState("");
  const [detailTab, setDetailTab] = useState("details");
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [newRmName, setNewRmName] = useState("");
  const [editStaff, setEditStaff] = useState([]);
  const [editVendors, setEditVendors] = useState([]);
  const [addrSaving, setAddrSaving] = useState(false);
  const [addrForm, setAddrForm] = useState({});
  const fetchOrder = useCallback(async () => {
    try {
      const [oRes, tRes, pRes, rRes] = await Promise.all([
        fetch(`${API}/orders/${id}`),
        fetch(`${API}/orders/${id}/timeline`),
        fetch(`${API}/orders/${id}/proofs`),
        fetch(`${API}/riders?active_only=true`),
      ]);
      setOrder(await oRes.json());
      setTimeline(await tRes.json());
      setProofs(await pRes.json());
      setRiders(await rRes.json());
    } catch {
      toast.error("Failed to load order");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  useEffect(() => {
    if (order) {
      setAddrForm({
        pickup_address: order.pickup_address || "",
        delivery_address: order.delivery_address || "",
        pickup_time: order.pickup_time || "",
        delivery_time: order.delivery_time || "",
      });
    }
  }, [order]);

  const saveAddrField = async (field, value) => {
    setAddrSaving(true);
    try {
      const res = await fetch(`${API}/orders/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [field]: value }),
      });
      if (!res.ok) throw new Error("Failed to save");
      toast.success("Updated!");
      fetchOrder();
    } catch {
      toast.error("Failed to save");
    } finally {
      setAddrSaving(false);
    }
  };

  const advanceStatus = async (nextStatus, skipGates = false) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/orders/${id}/status`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: nextStatus, skip_gates: skipGates }),
      });
      if (!res.ok) {
        let msg = "Failed to update status";
        try { const d = await res.json(); msg = d.detail || msg; } catch {}
        throw new Error(msg);
      }
      toast.success(`Status updated to ${STATUS_LABELS[nextStatus]}`);
      fetchOrder();
    } catch (e) {
      toast.error(e.message || "Failed to update status");
    } finally {
      setActionLoading(false);
    }
  };

  const assignRider = async (type) => {
    if (!selectedRiderId) { toast.error("Select a rider first"); return; }
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/orders/${id}/assign-rider`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rider_id: selectedRiderId, assignment_type: type }),
      });
      if (!res.ok) { let msg = "Failed to assign rider"; try { const d = await res.json(); msg = d.detail || msg; } catch {} throw new Error(msg); }
      toast.success(`${type === "pickup" ? "Pickup" : "Delivery"} rider assigned!`);
      setSelectedRiderId("");
      fetchOrder();
    } catch (e) {
      toast.error(e.message || "Failed to assign rider");
    } finally {
      setActionLoading(false);
    }
  };

  const autoAssignRider = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/riders/next`);
      if (!res.ok) throw new Error("No active riders available");
      const rider = await res.json();
      setSelectedRiderId(rider.id);
      toast.success(`Auto-selected: ${rider.full_name}`);
    } catch (e) {
      toast.error(e.message || "No riders available");
    } finally {
      setActionLoading(false);
    }
  };

  const markPayout = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/orders/${id}/payout`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reference: payoutRef, payee_name: order.payee_name || "" }),
      });
      if (!res.ok) { let msg = "Failed to mark payout"; try { const d = await res.json(); msg = d.detail || msg; } catch {} throw new Error(msg); }
      toast.success("Payout marked complete!");
      setPayoutRef("");
      fetchOrder();
    } catch (e) {
      toast.error(e.message || "Failed to mark payout");
    } finally {
      setActionLoading(false);
    }
  };

  const createRM = async (name) => {
    if (!name.trim()) return;
    try {
      const res = await fetch(`${API}/staff`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ full_name: name.trim(), team: "Relations Manager" }),
      });
      if (!res.ok) throw new Error("Failed");
      const created = await res.json();
      setEditStaff(prev => [...prev, created]);
      setEditForm(p => ({ ...p, rm_ids: [...(p.rm_ids || []), created.id] }));
      setNewRmName("");
      toast.success(`RM "${created.full_name}" created`);
    } catch {
      toast.error("Failed to create RM");
    }
  };

  const openEditDialog = async () => {
    setEditForm({
      order_name: order.order_name || "",
      customer_name: order.customer_name || "",
      customer_phone: order.customer_phone || "",
      contact_id: order.contact_id || "",
      service_type: order.service_type || "",
      journey_type: order.journey_type || "home_to_shop",
      merchant_id: order.merchant_id || "",
      rm_ids: order.rm_ids || [],
      notes: order.notes || "",
      pickup_address: order.pickup_address || "",
      delivery_address: order.delivery_address || "",
      pickup_time: order.pickup_time || "",
      delivery_time: order.delivery_time || "",
    });
    try {
      const [vRes, sRes] = await Promise.all([
        fetch(`${API}/vendors`),
        fetch(`${API}/staff?team=Relations+Manager`),
      ]);
      setEditVendors(await vRes.json());
      setEditStaff(await sRes.json());
    } catch {}
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setEditSaving(true);
    try {
      const { plane_url, ...payload } = editForm;
      if (order.plane_url) delete payload.rm_ids;
      const res = await fetch(`${API}/orders/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let msg = "Failed to save";
        try { const d = await res.json(); msg = d.detail || msg; } catch {}
        throw new Error(msg);
      }
      toast.success("Order updated!");
      setEditOpen(false);
      fetchOrder();
    } catch (e) {
      toast.error(e.message || "Failed to update order");
    } finally {
      setEditSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-pulse text-slate-500">Loading order...</div></div>;
  if (!order) return <div className="text-center py-20"><p className="text-slate-500 mb-4">Order not found.</p><Link to="/orders"><Button variant="outline">Back</Button></Link></div>;

  const jt = order.journey_type || "home_to_shop";
  const nextMap = NEXT_MAP[jt] || NEXT_MAP.home_to_shop;
  const nextStatus = nextMap[order.status];
  const labels = jt === "c2c" ? ACTION_LABELS_C2C : jt === "shop_to_home" ? ACTION_LABELS_S2H : ACTION_LABELS;
  const nextLabel = labels[order.status];
  const prevMap = PREV_MAP[jt] || PREV_MAP.home_to_shop;
  const prevStatus = prevMap[order.status] ?? null;
  const skipMap = SKIP_MAP[jt] || {};
  const skipTarget = skipMap[order.status] ?? null;

  // When does the order need a pickup rider?
  const needsPickupRider = order.status === "pickup_assignment_needed" && (jt === "home_to_shop" || jt === "c2c" || jt === "shop_to_home");
  // When does the order need payout to be marked?
  // H2S + S2H: show payout form at invoice_generated (skip signature step)
  // Also catch legacy payout_pending orders where payout wasn't marked yet
  const needsPayoutMark = jt !== "c2c" && (
    order.status === "invoice_generated" ||
    (order.status === "payout_pending" && order.payout_status !== "completed")
  );
  // When does the order need a delivery rider? (H2S only — S2H auto-assigns pickup rider)
  const needsDeliveryRider = (
    order.status === "delivery_assignment_needed" &&
    jt === "home_to_shop"
  );
  // Is the advance button blocked because rider hasn't submitted pickup details?
  const pickupDetailsMissing = (
    order.status === "pickup_assigned" &&
    (jt === "home_to_shop" || jt === "c2c" || jt === "shop_to_home") &&
    !order.items_collected
  );
  // Merchant drop gate (H2S): rider must submit drop-off details before RM can advance from pickup_complete
  const merchantDetailsMissing = (
    order.status === "pickup_complete" &&
    jt === "home_to_shop" &&
    !order.merchant_reached_time && !order.items_submitted
  );
  // S2H delivery gate: rider must deliver to customer and submit details before RM can mark delivered
  const s2hDeliveryMissing = (
    order.status === "delivery_assigned" &&
    jt === "shop_to_home" &&
    !order.delivered_items
  );
  // Is the advance button blocked because C2C rider hasn't submitted delivery details yet?
  const c2cDeliveryMissing = (
    order.status === "pickup_complete" &&
    jt === "c2c" &&
    !order.delivered_items
  );
  // Is the Close Order button blocked because rider hasn't submitted delivery details?
  const deliveryDetailsMissing = (
    order.status === "delivered" &&
    !order.delivered_items
  );
  return (
    <div className="max-w-5xl mx-auto space-y-5">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <Link to="/orders">
          <Button variant="ghost" size="sm" className="text-slate-500 -ml-2">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Orders
          </Button>
        </Link>
        <span className={`px-3 py-1 rounded-full text-xs font-bold ${STATUS_COLORS[order.status] || "bg-slate-100 text-slate-700"}`}>
          {STATUS_LABELS[order.status] || order.status}
        </span>
      </div>

      {/* Order Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900">{order.order_name || order.order_number}</h1>
            <span className="text-xs px-2 py-0.5 rounded font-medium bg-slate-100 text-slate-600">
              {JOURNEY_LABELS[jt] || jt}
            </span>
          </div>
          {order.order_name && <p className="text-xs font-mono text-slate-400">{order.order_number}</p>}
          <p className="text-sm text-slate-500 mt-0.5">{order.customer_name} &middot; {order.service_type} &middot; {order.order_date}</p>
          {(order.rm_names || []).length > 0 && (
            <div className="flex items-center gap-1.5 mt-1.5">
              <span className="text-[10px] text-slate-400 uppercase tracking-wide">RM:</span>
              {order.rm_names.map((name, i) => (
                <span key={i} className="text-[11px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded font-medium">{name}</span>
              ))}
            </div>
          )}
          {(order.pickup_rider_name || order.delivery_rider_name) && (
            <div className="flex items-center gap-1.5 mt-1.5">
              <span className="text-[10px] text-slate-400 uppercase tracking-wide">Riders:</span>
              {order.pickup_rider_name && (
                <span className="text-[11px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded font-medium flex items-center gap-0.5">
                  <Truck className="w-3 h-3" /> {order.pickup_rider_name}
                </span>
              )}
              {order.delivery_rider_name && order.delivery_rider_name !== order.pickup_rider_name && (
                <span className="text-[11px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded font-medium flex items-center gap-0.5">
                  <Truck className="w-3 h-3" /> {order.delivery_rider_name}
                </span>
              )}
            </div>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={openEditDialog}>
          <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
        </Button>
      </div>

      {/* Tabs — only shown when plane_url exists */}
      {order.plane_url && (
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
          {[
            { key: "details", label: "Details", icon: FileText },
            { key: "plane", label: "Plane", icon: Globe },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setDetailTab(key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                detailTab === key ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Plane iframe tab */}
      {detailTab === "plane" && order.plane_url && (
        <div className="-mx-4 sm:-mx-6 lg:-mx-8 -mb-4 sm:-mb-6 lg:-mb-8 overflow-hidden bg-white border-t border-slate-200" style={{ height: "calc(100vh - 160px)" }}>
          <iframe
            src={order.plane_url}
            title="Plane"
            className="w-full h-full border-0"
            allow="clipboard-read; clipboard-write"
          />
        </div>
      )}

      {/* Details tab (default) */}
      {(detailTab === "details" || !order.plane_url) && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left column — details */}
        <div className="lg:col-span-2 space-y-4">

          {/* Action Card */}
          {(nextStatus || needsPickupRider || needsPayoutMark || needsDeliveryRider || skipTarget || prevStatus || pickupDetailsMissing || c2cDeliveryMissing || deliveryDetailsMissing) && (
            <Card className="border-[#0047FF]/30 bg-blue-50/50">
              <CardContent className="p-4">
                <p className="text-xs font-bold text-[#0047FF] uppercase tracking-wide mb-3">Next Action</p>

                {needsPickupRider && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Assign Pickup Rider</Label>
                      <button onClick={autoAssignRider} disabled={actionLoading} className="text-[10px] text-[#0047FF] font-semibold flex items-center gap-0.5 hover:underline disabled:opacity-50">
                        <Zap className="w-3 h-3" /> Auto-assign
                      </button>
                    </div>
                    <div className="flex gap-2 items-center">
                      <Select value={selectedRiderId} onValueChange={setSelectedRiderId}>
                        <SelectTrigger className="h-9 text-sm bg-white flex-1"><SelectValue placeholder="Select rider" /></SelectTrigger>
                        <SelectContent>
                          {riders.map(r => <SelectItem key={r.id} value={r.id}>{r.full_name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button size="sm" onClick={() => assignRider("pickup")} disabled={actionLoading || !selectedRiderId} className="bg-[#0047FF] hover:bg-[#0033CC]">
                        <Truck className="w-3.5 h-3.5 mr-1" /> Assign
                      </Button>
                    </div>
                  </div>
                )}

                {needsPayoutMark && (
                  // H2S: signing is deferred to shop-pickup (handled by the
                  // delivery rider), so payout is never blocked by signature.
                  // S2H: merchant signature is still required before payout.
                  jt === "shop_to_home" && order.signing_token && order.order_signature_status !== "signed" ? (
                    <div className="rounded-lg bg-orange-50 border border-orange-200 px-3 py-2.5 space-y-2">
                      <div className="flex items-start gap-2">
                        <PenLine className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-semibold text-orange-800">Waiting for merchant signature</p>
                          <p className="text-[11px] text-orange-700 mt-0.5">Payout cannot be marked until the merchant signs the invoice. Share the link below with the rider.</p>
                        </div>
                      </div>
                      <div className="flex gap-2 items-center">
                        <code className="text-xs bg-white border border-orange-200 px-2 py-1 rounded flex-1 truncate text-slate-600">
                          {`${window.location.origin}/order-sign/${order.signing_token}`}
                        </code>
                        <Button size="sm" variant="outline" className="shrink-0" onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/order-sign/${order.signing_token}`);
                          toast.success("Signing link copied!");
                        }}>
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label className="text-xs">Mark Payout Complete</Label>
                      {jt === "home_to_shop" && order.signing_token && order.order_signature_status !== "signed" && (
                        <p className="text-[11px] text-slate-500 flex items-start gap-1.5">
                          <PenLine className="w-3 h-3 text-slate-400 shrink-0 mt-0.5" />
                          Merchant signature is collected by the delivery rider at shop pickup — you can mark the payout now.
                        </p>
                      )}
                      <div className="flex gap-2 items-end">
                        <Input
                          value={payoutRef}
                          onChange={(e) => setPayoutRef(e.target.value)}
                          placeholder="Payment reference (optional)"
                          className="h-9 text-sm bg-white flex-1"
                        />
                        <Button size="sm" onClick={markPayout} disabled={actionLoading} className="bg-pink-600 hover:bg-pink-700">
                          <Wallet className="w-3.5 h-3.5 mr-1" /> Mark Paid
                        </Button>
                      </div>
                    </div>
                  )
                )}

                {needsDeliveryRider && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Assign Delivery Rider</Label>
                      <button onClick={autoAssignRider} disabled={actionLoading} className="text-[10px] text-[#0047FF] font-semibold flex items-center gap-0.5 hover:underline disabled:opacity-50">
                        <Zap className="w-3 h-3" /> Auto-assign
                      </button>
                    </div>
                    <div className="flex gap-2 items-center">
                      <Select value={selectedRiderId} onValueChange={setSelectedRiderId}>
                        <SelectTrigger className="h-9 text-sm bg-white flex-1"><SelectValue placeholder="Select rider" /></SelectTrigger>
                        <SelectContent>
                          {riders.map(r => <SelectItem key={r.id} value={r.id}>{r.full_name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button size="sm" onClick={() => assignRider("delivery")} disabled={actionLoading || !selectedRiderId} className="bg-cyan-600 hover:bg-cyan-700">
                        <ArrowRight className="w-3.5 h-3.5 mr-1" /> Assign
                      </Button>
                    </div>
                  </div>
                )}

                {nextStatus && nextLabel && !needsPickupRider && !needsDeliveryRider && !needsPayoutMark && (
                  (pickupDetailsMissing || s2hDeliveryMissing || c2cDeliveryMissing || deliveryDetailsMissing) ? (
                    <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 flex items-start gap-2">
                      <Camera className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-semibold text-amber-800">Waiting for rider</p>
                        <p className="text-[11px] text-amber-700 mt-0.5">
                          {pickupDetailsMissing
                            ? "Rider must submit pickup time and items collected before this order can be advanced."
                            : s2hDeliveryMissing
                            ? "Rider must deliver to the customer and submit delivery details before this order can be marked delivered."
                            : c2cDeliveryMissing
                            ? "Rider must submit delivery details before this order can be closed."
                            : "Rider must submit delivery drop-off details before this order can be closed."}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <Button size="sm" onClick={() => advanceStatus(nextStatus)} disabled={actionLoading} className="bg-[#0047FF] hover:bg-[#0033CC] w-full mt-2">
                      <ChevronRight className="w-4 h-4 mr-1" /> {nextLabel}
                    </Button>
                  )
                )}

                {skipTarget && (
                  <div className="border-t border-blue-200 mt-3 pt-3">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1.5">Skip</p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => advanceStatus(skipTarget, true)}
                      disabled={actionLoading}
                      className="w-full text-slate-600 border-slate-300 hover:bg-slate-50"
                    >
                      <SkipForward className="w-3.5 h-3.5 mr-1" /> Skip to {STATUS_LABELS[skipTarget]}
                    </Button>
                  </div>
                )}

                {prevStatus && (
                  <div className={`${(nextStatus && nextLabel && !needsPickupRider && !needsDeliveryRider) || needsPickupRider || needsPayoutMark || needsDeliveryRider || skipTarget ? "border-t border-blue-200 mt-3 pt-3" : ""}`}>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1.5">Move Back</p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => advanceStatus(prevStatus)}
                      disabled={actionLoading}
                      className="w-full text-amber-700 border-amber-300 hover:bg-amber-50"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" /> Back to {STATUS_LABELS[prevStatus]}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Rider View Preview — collapsible, shows what the rider sees for this order */}
          {(order.pickup_rider_name || order.delivery_rider_name) && (() => {
            const isMapUrl = (s) => /^https?:\/\//i.test(s || "");
            const deliveryPhase = ["delivery_assigned", "delivered"].includes(order.status);
            const activeRider = deliveryPhase ? (order.delivery_rider_name || order.pickup_rider_name) : order.pickup_rider_name;
            const h2sSwap = jt === "home_to_shop" && deliveryPhase;
            const pickupAddr = h2sSwap ? (order.merchant_address || order.delivery_address || "") : (order.pickup_address || "");
            const dropAddr = h2sSwap ? (order.pickup_address || "") : (order.delivery_address || "");
            return (
              <Collapsible>
                <Card className="border-slate-200">
                  <CollapsibleTrigger asChild>
                    <button className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg bg-violet-100 flex items-center justify-center">
                          <Bike className="w-3.5 h-3.5 text-violet-600" />
                        </div>
                        <div className="text-left">
                          <p className="text-sm font-semibold text-slate-700">Rider View</p>
                          <p className="text-[10px] text-slate-400">What {activeRider || "the rider"} sees for this order</p>
                        </div>
                      </div>
                      <ChevronDown className="w-4 h-4 text-slate-400 transition-transform [[data-state=open]>&]:rotate-180" />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="px-4 pb-4 space-y-3">
                      {/* Simulated rider info grid */}
                      <div className="rounded-xl bg-slate-50 divide-y divide-slate-100 text-sm">
                        <div className="flex items-center gap-3 px-3 py-2.5">
                          <User className="w-4 h-4 text-slate-400 shrink-0" />
                          <div>
                            <p className="text-[10px] text-slate-400 uppercase tracking-wide">Customer</p>
                            <p className="text-sm font-medium text-slate-800">{order.customer_name}</p>
                          </div>
                        </div>
                        {order.customer_phone && (
                          <div className="flex items-center gap-3 px-3 py-2.5">
                            <Phone className="w-4 h-4 text-blue-500 shrink-0" />
                            <div>
                              <p className="text-[10px] text-slate-400 uppercase tracking-wide">Call Customer</p>
                              <p className="text-sm font-medium text-blue-600">{order.customer_phone}</p>
                            </div>
                          </div>
                        )}
                        {order.merchant_name && (
                          <div className="flex items-center gap-3 px-3 py-2.5">
                            <Store className="w-4 h-4 text-slate-400 shrink-0" />
                            <div>
                              <p className="text-[10px] text-slate-400 uppercase tracking-wide">Merchant</p>
                              <p className="text-sm font-medium text-slate-800">{order.merchant_name}</p>
                            </div>
                          </div>
                        )}
                        {order.merchant_address && (
                          <div className="flex items-start gap-3 px-3 py-2.5">
                            <MapPin className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                            <div>
                              <p className="text-[10px] text-slate-400 uppercase tracking-wide">Merchant Address</p>
                              <p className="text-sm text-slate-700">{order.merchant_address}</p>
                            </div>
                          </div>
                        )}
                        {pickupAddr && (
                          <div className="flex items-start gap-3 px-3 py-2.5">
                            <MapPin className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                            <div>
                              <p className="text-[10px] text-slate-400 uppercase tracking-wide">Pickup Address</p>
                              {isMapUrl(pickupAddr) ? (
                                <a href={pickupAddr} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 underline break-all">{pickupAddr}</a>
                              ) : (
                                <p className="text-sm text-slate-700">{pickupAddr}</p>
                              )}
                            </div>
                          </div>
                        )}
                        {dropAddr && (
                          <div className="flex items-start gap-3 px-3 py-2.5">
                            <MapPin className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                            <div>
                              <p className="text-[10px] text-slate-400 uppercase tracking-wide">Drop Address</p>
                              {isMapUrl(dropAddr) ? (
                                <a href={dropAddr} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 underline break-all">{dropAddr}</a>
                              ) : (
                                <p className="text-sm text-slate-700">{dropAddr}</p>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-3 px-3 py-2.5">
                          <Clock className="w-4 h-4 text-slate-400 shrink-0" />
                          <div>
                            <p className="text-[10px] text-slate-400 uppercase tracking-wide">Service · Journey</p>
                            <p className="text-sm text-slate-700">{order.service_type} · {JOURNEY_LABELS[jt] || jt}</p>
                          </div>
                        </div>
                        {(order.rm_names || []).length > 0 && (
                          <div className="px-3 py-2.5">
                            <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1">RM</p>
                            <div className="flex flex-wrap gap-1.5">
                              {order.rm_names.map((name, i) => (
                                <span key={i} className="text-[11px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded font-medium">{name}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {activeRider && (
                          <div className="flex items-center gap-3 px-3 py-2.5">
                            <Bike className="w-4 h-4 text-violet-500 shrink-0" />
                            <div>
                              <p className="text-[10px] text-slate-400 uppercase tracking-wide">
                                {deliveryPhase ? "Delivery Rider" : "Pickup Rider"}
                              </p>
                              <p className="text-sm font-medium text-slate-800">{activeRider}</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Maps buttons */}
                      <div className="space-y-2">
                        {pickupAddr && isMapUrl(pickupAddr) && (
                          <a href={pickupAddr} target="_blank" rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 w-full py-2 rounded-xl border border-blue-300 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100">
                            <MapPin className="w-4 h-4 text-blue-500" />
                            Navigate to Pickup
                          </a>
                        )}
                        {dropAddr && isMapUrl(dropAddr) && (
                          <a href={dropAddr} target="_blank" rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 w-full py-2 rounded-xl border border-emerald-300 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100">
                            <MapPin className="w-4 h-4 text-emerald-500" />
                            Navigate to Drop
                          </a>
                        )}
                        {order.merchant_maps_link && (
                          <a href={order.merchant_maps_link} target="_blank" rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 w-full py-2 rounded-xl border border-slate-300 text-sm font-medium text-slate-700 bg-white hover:bg-slate-50">
                            <MapPin className="w-4 h-4 text-red-500" />
                            Navigate to Merchant
                          </a>
                        )}
                      </div>
                    </div>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            );
          })()}

          {/* Pickup Details — read-only, populated by rider. Show whenever rider has submitted anything,
              across all journey types and all downstream statuses so RM can always see what was picked up. */}
          {(order.pickup_time || order.items_collected || proofs.some(p => p.proof_type === "pickup_from_customer")) && (
            <Card className="border-amber-200 bg-amber-50/30">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                  <Package className="w-4 h-4" /> Pickup Details
                  <span className="ml-auto flex items-center gap-1 text-[10px] font-normal text-emerald-600">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Submitted by rider
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  {order.pickup_rider_name && (
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">Rider</p>
                      <p className="font-medium text-slate-800">{order.pickup_rider_name}</p>
                    </div>
                  )}
                  {order.pickup_time && (
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">Pickup Time</p>
                      <p className="font-medium text-slate-800">{new Date(order.pickup_time).toLocaleString()}</p>
                    </div>
                  )}
                  {order.pickup_address && (
                    <div className="col-span-2">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">Pickup Address</p>
                      <p className="text-slate-700">{order.pickup_address}</p>
                    </div>
                  )}
                  {order.items_collected && (
                    <div className="col-span-2">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">Items Collected</p>
                      <p className="text-slate-700">{order.items_collected}</p>
                    </div>
                  )}
                </div>
                {/* Pickup photos */}
                {proofs.filter(p => p.proof_type === "pickup_from_customer").length > 0 && (
                  <div className="border-t border-amber-100 pt-2">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-2">Pickup Photo</p>
                    <div className="flex gap-2 flex-wrap">
                      {proofs.filter(p => p.proof_type === "pickup_from_customer").map(p => (
                        <img key={p.id} src={p.image_data} alt="pickup proof" className="w-24 h-24 object-cover rounded-lg border border-amber-200" />
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Merchant Arrival Details — read-only, visible once rider submits drop-off info */}
          {(order.status === "pickup_complete" || order.status === "at_merchant" || order.status === "invoice_generated" || order.status === "payout_pending" || order.status === "delivery_assigned" || order.status === "delivered" || order.status === "closed") && jt === "home_to_shop" && (order.merchant_reached_time || order.shop_location || order.items_submitted || proofs.some(p => p.proof_type === "drop_at_merchant")) && (
            <Card className="border-orange-200 bg-orange-50/30">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-semibold text-orange-800 flex items-center gap-2">
                  <Store className="w-4 h-4" /> Shop Arrival Details
                  <span className="ml-auto flex items-center gap-1 text-[10px] font-normal text-emerald-600">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Submitted by rider
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  {order.pickup_rider_name && (
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">Rider</p>
                      <p className="font-medium text-slate-800">{order.pickup_rider_name}</p>
                    </div>
                  )}
                  {order.merchant_reached_time && (
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">Reached Shop At</p>
                      <p className="font-medium text-slate-800">{new Date(order.merchant_reached_time).toLocaleString()}</p>
                    </div>
                  )}
                  {order.shop_location && (
                    <div className="col-span-2">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">Shop Name / Location</p>
                      <p className="text-slate-700">{order.shop_location}</p>
                    </div>
                  )}
                  {order.items_submitted && (
                    <div className="col-span-2">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">Items Submitted</p>
                      <p className="text-slate-700">{order.items_submitted}</p>
                    </div>
                  )}
                </div>
                {proofs.filter(p => p.proof_type === "drop_at_merchant").length > 0 && (
                  <div className="border-t border-orange-100 pt-2">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-2">Drop-off Photo</p>
                    <div className="flex gap-2 flex-wrap">
                      {proofs.filter(p => p.proof_type === "drop_at_merchant").map(p => (
                        <img key={p.id} src={p.image_data} alt="drop-off proof" className="w-24 h-24 object-cover rounded-lg border border-orange-200" />
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Shop Pickup Details — read-only, shown once delivery rider collects from merchant */}
          {(jt === "home_to_shop" || jt === "shop_to_home") && (order.shop_pickup_time || order.shop_pickup_items || proofs.some(p => p.proof_type === "pickup_from_merchant")) && (
            <Card className="border-cyan-200 bg-cyan-50/30">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-semibold text-cyan-800 flex items-center gap-2">
                  <Store className="w-4 h-4" /> Shop Pickup Details
                  <span className="ml-auto flex items-center gap-1 text-[10px] font-normal text-emerald-600">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Submitted by rider
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  {order.delivery_rider_name && (
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">Rider</p>
                      <p className="font-medium text-slate-800">{order.delivery_rider_name}</p>
                    </div>
                  )}
                  {order.shop_pickup_time && (
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">Picked Up at</p>
                      <p className="font-medium text-slate-800">{new Date(order.shop_pickup_time).toLocaleString()}</p>
                    </div>
                  )}
                  {order.shop_pickup_location && (
                    <div className="col-span-2">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">Shop Location</p>
                      <p className="text-slate-700">{order.shop_pickup_location}</p>
                    </div>
                  )}
                  {order.shop_pickup_items && (
                    <div className="col-span-2">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">Items Collected</p>
                      <p className="text-slate-700">{order.shop_pickup_items}</p>
                    </div>
                  )}
                </div>
                {proofs.filter(p => p.proof_type === "pickup_from_merchant").length > 0 && (
                  <div className="border-t border-cyan-100 pt-2">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-2">Photo</p>
                    <div className="flex gap-2 flex-wrap">
                      {proofs.filter(p => p.proof_type === "pickup_from_merchant").map(p => (
                        <img key={p.id} src={p.image_data} alt="shop pickup" className="w-24 h-24 object-cover rounded-lg border border-cyan-200" />
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Delivery Details — read-only, shown once rider completes delivery (all journey types incl. C2C) */}
          {(order.delivery_time || order.delivered_items || proofs.some(p => p.proof_type === "delivered_to_customer")) && (
            <Card className="border-emerald-200 bg-emerald-50/30">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-semibold text-emerald-800 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> Delivery Details
                  <span className="ml-auto flex items-center gap-1 text-[10px] font-normal text-emerald-600">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Delivered
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  {order.delivery_rider_name && (
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">Rider</p>
                      <p className="font-medium text-slate-800">{order.delivery_rider_name}</p>
                    </div>
                  )}
                  {order.delivery_time && (
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">Delivered at</p>
                      <p className="font-medium text-slate-800">{new Date(order.delivery_time).toLocaleString()}</p>
                    </div>
                  )}
                  {order.delivery_address && (
                    <div className="col-span-2">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">Delivery Address</p>
                      <p className="text-slate-700">{order.delivery_address}</p>
                    </div>
                  )}
                  {order.delivered_items && (
                    <div className="col-span-2">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-0.5">Items Delivered</p>
                      <p className="text-slate-700">{order.delivered_items}</p>
                    </div>
                  )}
                </div>
                {proofs.filter(p => p.proof_type === "delivered_to_customer").length > 0 && (
                  <div className="border-t border-emerald-100 pt-2">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-2">Proof of Delivery</p>
                    <div className="flex gap-2 flex-wrap">
                      {proofs.filter(p => p.proof_type === "delivered_to_customer").map(p => (
                        <img key={p.id} src={p.image_data} alt="delivery proof" className="w-24 h-24 object-cover rounded-lg border border-emerald-200" />
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Customer Info */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm font-semibold text-slate-700">Customer</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-2 text-sm">
              <div className="flex items-center gap-2"><User className="w-4 h-4 text-slate-400" /> {order.customer_name}</div>
              <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" /> {order.customer_phone}</div>
              {order.contact_id && <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-slate-400" /> Contact ID: {order.contact_id}</div>}
            </CardContent>
          </Card>

          {/* Merchant Info */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm font-semibold text-slate-700">Merchant</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-2 text-sm">
              {order.merchant_name ? (
                <>
                  <div className="flex items-center gap-2"><Store className="w-4 h-4 text-slate-400" /> {order.merchant_name}</div>
                  {order.merchant_contact && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" /> {order.merchant_contact}</div>}
                  {order.merchant_address && <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-slate-400 mt-0.5" /> <span className="text-slate-600">{order.merchant_address}</span></div>}
                  {order.merchant_maps_link && (
                    <a href={order.merchant_maps_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm">
                      <ExternalLink className="w-4 h-4" /> Open in Maps
                    </a>
                  )}
                </>
              ) : (
                <p className="text-slate-400 italic">No merchant assigned yet</p>
              )}
            </CardContent>
          </Card>

          {/* Addresses & Schedule */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Addresses & Schedule
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3 text-sm">
              <div className="space-y-1">
                <Label className="text-[10px] text-slate-400 uppercase tracking-wide">Pickup Address</Label>
                <Input
                  className="h-8 text-sm"
                  placeholder="Pickup address or Maps link"
                  value={addrForm.pickup_address || ""}
                  onChange={(e) => setAddrForm(p => ({ ...p, pickup_address: e.target.value }))}
                  onBlur={() => {
                    if (addrForm.pickup_address !== (order.pickup_address || ""))
                      saveAddrField("pickup_address", addrForm.pickup_address);
                  }}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-slate-400 uppercase tracking-wide">Drop Address (Customer / Vendor)</Label>
                <Input
                  className="h-8 text-sm"
                  placeholder="Drop-off address or Maps link"
                  value={addrForm.delivery_address || ""}
                  onChange={(e) => setAddrForm(p => ({ ...p, delivery_address: e.target.value }))}
                  onBlur={() => {
                    if (addrForm.delivery_address !== (order.delivery_address || ""))
                      saveAddrField("delivery_address", addrForm.delivery_address);
                  }}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[10px] text-slate-400 uppercase tracking-wide">Pickup Time</Label>
                  <Input
                    type="datetime-local"
                    className="h-8 text-sm"
                    value={addrForm.pickup_time || ""}
                    onChange={(e) => setAddrForm(p => ({ ...p, pickup_time: e.target.value }))}
                    onBlur={() => {
                      if (addrForm.pickup_time !== (order.pickup_time || ""))
                        saveAddrField("pickup_time", addrForm.pickup_time);
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] text-slate-400 uppercase tracking-wide">Delivery Time</Label>
                  <Input
                    type="datetime-local"
                    className="h-8 text-sm"
                    value={addrForm.delivery_time || ""}
                    onChange={(e) => setAddrForm(p => ({ ...p, delivery_time: e.target.value }))}
                    onBlur={() => {
                      if (addrForm.delivery_time !== (order.delivery_time || ""))
                        saveAddrField("delivery_time", addrForm.delivery_time);
                    }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Riders */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm font-semibold text-slate-700">Riders</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Pickup Rider</p>
                  {order.pickup_rider_name
                    ? <p className="font-medium text-slate-900">{order.pickup_rider_name}</p>
                    : <p className="text-slate-400 italic">Not assigned</p>
                  }
                </div>
                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Delivery Rider</p>
                  {order.delivery_rider_name
                    ? <p className="font-medium text-slate-900">{order.delivery_rider_name}</p>
                    : <p className="text-slate-400 italic">Not assigned</p>
                  }
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Create Invoice CTA — shown at invoice_generated when no invoice linked yet */}
          {order.status === "invoice_generated" && !order.customer_invoice_id && (
            <Card className="border-[#0047FF]/30 bg-blue-50/40">
              <CardContent className="p-4 flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-slate-800">Invoice not created yet</p>
                  <p className="text-xs text-slate-500 mt-0.5">Create the customer invoice — a signing link will be sent to the rider for merchant sign-off.</p>
                </div>
                <Button
                  size="sm"
                  className="bg-[#0047FF] hover:bg-[#0033CC] shrink-0"
                  onClick={() => navigate(`/create?order_id=${id}&customer_name=${encodeURIComponent(order.customer_name)}&phone=${encodeURIComponent(order.customer_phone)}&contact_id=${encodeURIComponent(order.contact_id || "")}`)}
                >
                  <Receipt className="w-3.5 h-3.5 mr-1.5" /> Create Invoice
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Merchant Signing Status — shown once invoice is linked */}
          {order.customer_invoice_id && order.signing_token && (
            <Card className={`border-2 ${order.order_signature_status === "signed" ? "border-emerald-200 bg-emerald-50/40" : "border-orange-200 bg-orange-50/30"}`}>
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <PenLine className="w-4 h-4" />
                  Merchant Signature
                  {order.order_signature_status === "signed" ? (
                    <span className="ml-auto flex items-center gap-1 text-[10px] font-normal text-emerald-600">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Signed
                    </span>
                  ) : (
                    <span className="ml-auto text-[10px] font-normal text-orange-600">Awaiting signature</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-2">
                {order.order_signature_status === "signed" ? (
                  <p className="text-xs text-emerald-700">
                    Merchant signed on {new Date(order.order_signed_at).toLocaleString()}
                  </p>
                ) : (
                  <>
                    <p className="text-xs text-slate-500">
                      {jt === "home_to_shop"
                        ? "The delivery rider will collect the merchant signature at shop pickup. Link is available here as a backup."
                        : "Share this link with the rider — they will present it to the merchant for signature."}
                    </p>
                    <div className="flex gap-2 items-center">
                      <code className="text-xs bg-white border px-2 py-1 rounded flex-1 truncate">
                        {`${window.location.origin}/order-sign/${order.signing_token}`}
                      </code>
                      <Button size="sm" variant="outline" onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/order-sign/${order.signing_token}`);
                        toast.success("Signing link copied!");
                      }}>
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </>
                )}
                {/* Customer invoice link */}
                <div className="border-t border-slate-100 pt-2 flex items-center justify-between">
                  <span className="text-xs text-slate-400">Customer Invoice</span>
                  <Link to={`/invoice/${order.customer_invoice_id}`} className="text-xs text-[#0047FF] hover:underline flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> View / Download PDF
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}


          {/* Proof Uploads */}
          {proofs.length > 0 && (
            <Card className="border-slate-200">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Camera className="w-4 h-4" /> Proofs ({proofs.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {proofs.map((p) => (
                    <div key={p.id} className="border rounded-lg overflow-hidden">
                      <img src={p.image_data} alt={p.proof_type} className="w-full h-24 object-cover bg-slate-100" />
                      <div className="p-2">
                        <p className="text-[10px] font-medium text-slate-700">{p.proof_type.replace(/_/g, " ")}</p>
                        <p className="text-[10px] text-slate-400">{p.uploaded_by_name}</p>
                        {p.note && <p className="text-[10px] text-slate-500 mt-0.5 truncate">{p.note}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          {order.notes && (
            <Card className="border-slate-200">
              <CardHeader className="pb-2 pt-4 px-4"><CardTitle className="text-sm font-semibold text-slate-700">Notes</CardTitle></CardHeader>
              <CardContent className="px-4 pb-4 text-sm text-slate-600">{order.notes}</CardContent>
            </Card>
          )}
        </div>

        {/* Right column — Timeline */}
        <div className="space-y-4">
          <Card className="border-slate-200">
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Clock className="w-4 h-4" /> Timeline
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              {timeline.length === 0 ? (
                <p className="text-sm text-slate-400 italic">No events yet</p>
              ) : (
                <div className="relative">
                  <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-slate-200" />
                  <div className="space-y-4">
                    {[...timeline].reverse().map((event) => (
                      <div key={event.id} className="flex gap-3 relative">
                        <div className={`w-4 h-4 rounded-full border-2 bg-white shrink-0 mt-0.5 z-10 ${
                          event.event_type === "status_change" ? "border-[#0047FF]" :
                          event.event_type === "rider_assigned" ? "border-blue-500" :
                          event.event_type === "proof_uploaded" ? "border-green-500" :
                          event.event_type === "payout" ? "border-pink-500" :
                          "border-slate-400"
                        }`} />
                        <div className="min-w-0">
                          <p className="text-xs text-slate-800 leading-snug">{event.description}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[10px] text-slate-400">{event.actor}</span>
                            <span className="text-[10px] text-slate-300">&middot;</span>
                            <span className="text-[10px] text-slate-400">{new Date(event.timestamp).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      )}

      {/* Edit Order Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="space-y-1.5">
              <Label className="text-xs">Order Name</Label>
              <Input value={editForm.order_name || ""} onChange={(e) => setEditForm(p => ({ ...p, order_name: e.target.value }))} placeholder="e.g. Anika's Ironing Pickup" className="h-9 text-sm" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Customer Name</Label>
                <Input value={editForm.customer_name || ""} onChange={(e) => setEditForm(p => ({ ...p, customer_name: e.target.value }))} className="h-9 text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Phone</Label>
                <Input value={editForm.customer_phone || ""} onChange={(e) => setEditForm(p => ({ ...p, customer_phone: e.target.value }))} className="h-9 text-sm" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Contact ID</Label>
                <Input value={editForm.contact_id || ""} onChange={(e) => setEditForm(p => ({ ...p, contact_id: e.target.value }))} className="h-9 text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Service Type</Label>
                <Select value={editForm.service_type || ""} onValueChange={(v) => setEditForm(p => ({ ...p, service_type: v }))}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Ironing", "Wash and Fold", "Wash and Iron", "Dry Cleaning", "Tailoring", "Other"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Journey Type</Label>
                <Select value={editForm.journey_type || "home_to_shop"} onValueChange={(v) => {
                  setEditForm(p => {
                    const vendor = editVendors.find(vn => vn.id === p.merchant_id);
                    const addr = vendor?.address || "";
                    if (v === "shop_to_home") return { ...p, journey_type: v, pickup_address: addr };
                    if (v === "home_to_shop") return { ...p, journey_type: v, delivery_address: addr };
                    return { ...p, journey_type: v };
                  });
                }}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="home_to_shop">Home → Shop → Home</SelectItem>
                    <SelectItem value="shop_to_home">Shop → Home</SelectItem>
                    <SelectItem value="c2c">C2C</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Merchant</Label>
                <Select value={editForm.merchant_id || ""} onValueChange={(v) => {
                  const vendor = editVendors.find(vn => vn.id === v);
                  setEditForm(p => {
                    const jt = p.journey_type || "home_to_shop";
                    const addr = vendor?.address || "";
                    if (jt === "shop_to_home") return { ...p, merchant_id: v, pickup_address: addr };
                    if (jt === "home_to_shop") return { ...p, merchant_id: v, delivery_address: addr };
                    return { ...p, merchant_id: v };
                  });
                }}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {editVendors.map(v => <SelectItem key={v.id} value={v.id}>{v.merchant_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                Assigned RMs
                {order.plane_url && <span className="text-[10px] text-amber-600 font-normal">(locked — plane URL set)</span>}
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full h-9 text-sm justify-start font-normal" disabled={!!order.plane_url}>
                    {(editForm.rm_ids || []).length > 0
                      ? editForm.rm_ids.map(rid => editStaff.find(s => s.id === rid)?.full_name).filter(Boolean).join(", ")
                      : <span className="text-muted-foreground">Select RMs...</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-2" align="start">
                  {editStaff.map(s => (
                    <label key={s.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm">
                      <Checkbox
                        checked={(editForm.rm_ids || []).includes(s.id)}
                        onCheckedChange={(checked) => {
                          setEditForm(p => ({
                            ...p,
                            rm_ids: checked ? [...(p.rm_ids || []), s.id] : (p.rm_ids || []).filter(x => x !== s.id),
                          }));
                        }}
                      />
                      {s.full_name}
                    </label>
                  ))}
                  <div className="border-t mt-1 pt-1.5 flex gap-1.5 px-1">
                    <Input
                      value={newRmName}
                      onChange={(e) => setNewRmName(e.target.value)}
                      placeholder="New RM name..."
                      className="h-7 text-xs flex-1"
                      onKeyDown={(e) => e.key === "Enter" && createRM(newRmName)}
                    />
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => createRM(newRmName)} disabled={!newRmName.trim()}>
                      <Plus className="w-3 h-3 mr-0.5" /> Add
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                Plane URL
                {order.plane_url && <span className="text-[10px] text-amber-600 font-normal">(locked)</span>}
              </Label>
              <Input
                value={editForm.plane_url || ""}
                onChange={(e) => setEditForm(p => ({ ...p, plane_url: e.target.value }))}
                placeholder="https://..."
                className="h-9 text-sm"
                disabled={!!order.plane_url}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Customer Pickup Address</Label>
              <Input
                value={editForm.pickup_address || ""}
                onChange={(e) => setEditForm(p => ({ ...p, pickup_address: e.target.value }))}
                placeholder="Address or Google Maps link"
                className="h-9 text-sm"
              />
              <p className="text-[10px] text-slate-400">Can be a street address or a link. Rider will see this as read-only.</p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Drop Address (Customer / Vendor)</Label>
              <Input
                value={editForm.delivery_address || ""}
                onChange={(e) => setEditForm(p => ({ ...p, delivery_address: e.target.value }))}
                placeholder="Drop-off address or Google Maps link"
                className="h-9 text-sm"
              />
              <p className="text-[10px] text-slate-400">Auto-filled from merchant. Can be a customer or vendor address.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Pickup Time</Label>
                <Input
                  type="datetime-local"
                  value={editForm.pickup_time || ""}
                  onChange={(e) => setEditForm(p => ({ ...p, pickup_time: e.target.value }))}
                  className="h-9 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Delivery Time</Label>
                <Input
                  type="datetime-local"
                  value={editForm.delivery_time || ""}
                  onChange={(e) => setEditForm(p => ({ ...p, delivery_time: e.target.value }))}
                  className="h-9 text-sm"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Notes</Label>
              <Input value={editForm.notes || ""} onChange={(e) => setEditForm(p => ({ ...p, notes: e.target.value }))} className="h-9 text-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={editSaving} className="bg-[#0047FF] hover:bg-[#0033CC]">
              {editSaving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
